import { motion, type Transition } from 'framer-motion'
import React from 'react'
import styled from 'styled-components'

interface Props {
  options: Option[]
  value: any
  onUpdate: (value: any) => void
  size?: 'sm' | 'md'
}

export const SegmentedControl: React.FC<Props> = props => {
  const { size = 'md' } = props
  return (
    <StyledPane
      $size={size}
      tabIndex={0}
      onKeyDown={(ev: any) => {
        const _getCurrentIndex = () => {
          return props.options.findIndex((el: any) => {
            return el.value === props.value
          })
        }

        const _addToIndex = (index: number, value: number) => {
          index += value
          return Math.max(Math.min(index, props.options.length - 1), 0)
        }

        if (ev.key === 'ArrowRight') {
          const index = _getCurrentIndex()
          const newIndex = _addToIndex(index, 1)
          props.onUpdate(props.options[newIndex].value)
        }

        if (ev.key === 'ArrowLeft') {
          const index = _getCurrentIndex()
          const newIndex = _addToIndex(index, -1)
          props.onUpdate(props.options[newIndex].value)
        }
      }}
    >
      <OptionsRow>
        {props.options.map(({ label, value }) => {
          const isSelected = props.value === value
          return (
            <Option
              key={`option-${label}`}
              $isSelected={isSelected}
              $size={size}
              onClick={() => {
                if (!isSelected) {
                  props.onUpdate(value)
                }
              }}
            >
              {isSelected && <StyledBackground className="knob" $size={size} />}
              <StyledText animate={{ opacity: isSelected ? 1 : 0.7 }}>
                <OptionLabel $size={size}>{label}</OptionLabel>
              </StyledText>
            </Option>
          )
        })}
        {/* No value selected... */}
        {props.value === undefined && <StyledBackground className="knob" $size={size} />}
      </OptionsRow>
    </StyledPane>
  )
}

interface Option {
  label: React.ReactNode
  value: any
}

const spring: Transition = {
  type: 'spring',
  stiffness: 400,
  damping: 30
}

const StyledPane = styled.div<{ $size: 'sm' | 'md' }>`
  border: ${({ $size }) => ($size === 'sm' ? '1px' : '2px')} solid
    ${({ theme }) => theme.colors.neutralGray};
  background: ${({ theme }) => theme.colors.neutralGray};
  border-radius: ${({ theme, $size }) =>
    $size === 'sm' ? theme.borderRadius.sm : theme.borderRadius.md}rem;
  margin-bottom: ${({ theme }) => theme.spacing.xs}rem;
  transition: 0.2s;
`

const OptionsRow = styled.div`
  display: flex;
  position: relative;
`

const Option = styled.div<{ $isSelected: boolean; $size: 'sm' | 'md' }>`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  position: relative;
  cursor: ${({ $isSelected }) => ($isSelected ? 'initial' : 'pointer')};
  min-height: ${({ $size }) => ($size === 'sm' ? '28px' : '40px')};
  padding: ${({ theme, $size }) =>
    $size === 'sm' ? `${theme.spacing.xxs}rem ${theme.spacing.xs}rem` : `0 ${theme.spacing.sm}rem`};
`

const StyledText = styled(motion.div)`
  position: relative;
  z-index: 2;
`

const StyledBackground = styled(motion.div).attrs({
  layoutId: 'segmented-bg',
  initial: false,
  transition: spring
})<{ $size?: 'sm' | 'md' }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
  border-radius: ${({ theme, $size }) =>
    $size === 'sm' ? theme.borderRadius.sm : theme.borderRadius.md}rem;
  z-index: 1;
`

const OptionLabel = styled.span<{ $size: 'sm' | 'md' }>`
  font-size: ${({ theme, $size }) => ($size === 'sm' ? `${theme.fontSize.sm}rem` : '16px')};
  font-weight: ${({ $size }) => ($size === 'sm' ? 500 : 600)};
  color: ${({ theme }) => theme.colors.neutralBlack};
  line-height: 1.2;
`
